// ============================================================================
// STUDIO CANETTE — ROUTEUR (History API)
//
// Maison, pour une raison précise : le poids. Le visiteur qui arrive sur la
// landing ne doit pas télécharger Three.js. Chaque route pointe donc sur un
// `import()` paresseux — Vite en fait un fragment séparé, et le studio n'entre
// en mémoire qu'au moment où quelqu'un ouvre /app.
//
// Trois choses que fait ce fichier et qu'on oublie souvent :
//   • il intercepte les clics sur les <a> internes, pour que le HTML reste du
//     HTML (un lien est un lien, pas un onclick) tout en évitant le rechargement ;
//   • il annule les rendus périmés : cliquer deux fois vite ne doit pas afficher
//     la page précédente par-dessus la nouvelle ;
//   • il restaure le défilement en haut, SAUF sur retour arrière, où le
//     navigateur a déjà rétabli la position et où l'écraser est agaçant.
// ============================================================================

import { h, el, clear } from './dom.js';
import { isAuthed, recoveryMode } from './session.js';
import { mountChrome } from './layout.js';
import { toastError } from './toast.js';
import { BRAND } from '../../shared/copy.js';

/** @type {{path:string, segs:string[], loader:Function, opts:Object}[]} */
const table = [];

let started = false;
let nav = 0;              // jeton de rendu : tout ce qui n'est pas le dernier est jeté
let pushes = 0;           // navigations empilées par nous — pour un back() honnête
let mounted = null;       // module de la page affichée, pour appeler son destroy()

const CHROME_DEFAULT = { header: true, footer: true, variant: 'public' };

/* ------------------------------------------------------------------ *
 * Déclaration                                                         *
 * ------------------------------------------------------------------ */

/**
 * Déclare une route.
 *
 * @param {string}   path    '/app' ou '/app/:id'
 * @param {Function} loader  () => import('./pages/x.js')
 * @param {Object}   [opts]
 * @param {boolean}  [opts.requireAuth] redirige vers /connexion?suite=…
 * @param {boolean}  [opts.guestOnly]   un connecté est renvoyé sur /app
 * @param {Object}   [opts.params]      paramètres statiques passés à la page
 * @param {Object}   [opts.chrome]      en-tête/pied par défaut si la page n'en exporte pas
 * @returns {Function} la fonction elle-même, pour enchaîner les déclarations
 */
export function route(path, loader, opts = {}) {
  table.push({ path, segs: path.split('/').filter(Boolean), loader, opts });
  return route;
}

export const currentPath = () => location.pathname;

/* ------------------------------------------------------------------ *
 * Correspondance                                                      *
 * ------------------------------------------------------------------ */

function match(pathname) {
  // '/app/' et '/app' sont la même page.
  const parts = pathname.split('/').filter(Boolean).map(decodeURIComponent);
  for (const r of table) {
    if (r.segs.length !== parts.length) continue;
    const params = {};
    let ok = true;
    for (let i = 0; i < r.segs.length; i++) {
      const s = r.segs[i];
      if (s.charCodeAt(0) === 58 /* : */) params[s.slice(1)] = parts[i];
      else if (s.toLowerCase() !== parts[i].toLowerCase()) { ok = false; break; }
    }
    if (ok) return { r, params };
  }
  return null;
}

/** Garde anti-redirection ouverte : on ne suit que des chemins internes. */
function safeSuite(v) {
  if (!v || typeof v !== 'string') return null;
  if (!v.startsWith('/') || v.startsWith('//')) return null;
  return v;
}

/* ------------------------------------------------------------------ *
 * Navigation                                                          *
 * ------------------------------------------------------------------ */

/* Mémoire de défilement.
   `history.scrollRestoration` est passé en 'manual' : dans une application où
   le contenu arrive après un import(), le navigateur restaure la position
   AVANT que la page n'existe, donc au mauvais endroit — ou pas du tout. On
   note nous-mêmes la position de chaque entrée d'historique et on la rétablit
   une fois la page montée. */
const scrollMem = new Map();
let lastKey = 0;
const keyOf = () => history.state?.sc ?? 0;
const remember = () => scrollMem.set(lastKey, window.scrollY || document.documentElement.scrollTop || 0);

export function navigate(to, { replace = false } = {}) {
  let url;
  try { url = new URL(String(to), location.href); }
  catch { return; }

  if (url.origin !== location.origin) { location.assign(url.href); return; }

  const here = url.pathname === location.pathname && url.search === location.search;
  const full = url.pathname + url.search + url.hash;

  // Même page, ancre différente : on empile pour que le retour arrière marche,
  // mais on ne redessine RIEN — re-rendre la landing pour aller à #faq ferait
  // sauter l'écran.
  if (here && url.hash !== location.hash) {
    remember();
    history.pushState({ sc: ++pushes }, '', full);
    lastKey = pushes;
    scrollToHash(url.hash);
    return;
  }
  if (here && url.hash === location.hash) return;

  remember();
  if (replace) history.replaceState({ sc: pushes }, '', full);
  else history.pushState({ sc: ++pushes }, '', full);
  render({ pop: false });
}

/** Retour arrière, avec une sortie de secours si l'onglet s'est ouvert ici. */
export function back() {
  if (pushes > 0) history.back();
  else navigate('/', { replace: true });
}

export function startRouter() {
  if (started) return;
  started = true;
  // On gère nous-mêmes le défilement : sinon le navigateur restaure la position
  // AVANT que la page asynchrone ne soit montée, donc au mauvais endroit.
  if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
  // À cet instant, la page affichée est encore l'ancienne : sa position est
  // donc encore lisible, et c'est le dernier moment pour la noter.
  window.addEventListener('popstate', () => { remember(); render({ pop: true }); });
  document.addEventListener('click', onDocumentClick);
  render({ pop: false });
}

/* ------------------------------------------------------------------ *
 * Interception des liens                                              *
 * ------------------------------------------------------------------ */

function onDocumentClick(e) {
  if (e.defaultPrevented || e.button !== 0) return;
  if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;   // nouvel onglet, téléchargement…

  const a = e.target?.closest?.('a[href]');
  if (!a) return;
  if (a.target && a.target !== '_self') return;
  if (a.hasAttribute('download') || a.hasAttribute('data-native')) return;
  if ((a.getAttribute('rel') || '').includes('external')) return;

  const href = a.getAttribute('href');
  if (!href || href.startsWith('mailto:') || href.startsWith('tel:') || href.startsWith('#')) return;

  const url = new URL(href, location.href);
  if (url.origin !== location.origin) return;

  e.preventDefault();
  navigate(url.pathname + url.search + url.hash);
}

/* ------------------------------------------------------------------ *
 * Rendu                                                               *
 * ------------------------------------------------------------------ */

function scrollToHash(hash) {
  if (!hash || hash === '#') { window.scrollTo(0, 0); return; }
  // L'ancre peut être posée par la page juste après nous : on laisse une frame.
  requestAnimationFrame(() => {
    const target = document.getElementById(decodeURIComponent(hash.slice(1)));
    if (target) target.scrollIntoView({ block: 'start', behavior: 'auto' });
    else window.scrollTo(0, 0);
  });
}

/** Le studio prend l'écran entier hors du flux : on le range à chaque sortie. */
function resetStudioRoot() {
  const root = document.getElementById('studioRoot');
  if (!root) return;
  root.hidden = true;
  clear(root);
}

async function render({ pop = false } = {}) {
  const token = ++nav;
  lastKey = keyOf();                 // l'historique a déjà bougé : on suit
  const path = location.pathname;
  const found = match(path);

  if (!found) { renderNotFound(); return; }

  const { r, params } = found;
  const opts = r.opts || {};

  // ── Gardes ────────────────────────────────────────────────────────────
  if (opts.requireAuth && !isAuthed()) {
    navigate('/connexion?suite=' + encodeURIComponent(path + location.search), { replace: true });
    return;
  }
  // Exception à `guestOnly` : un jeton de récupération dans l'URL. Quelqu'un
  // de déjà connecté qui clique le lien de son courriel doit pouvoir poser son
  // nouveau mot de passe, pas se faire expédier sur /app.
  if (opts.guestOnly && isAuthed() && !recoveryMode()) {
    const suite = safeSuite(new URLSearchParams(location.search).get('suite'));
    navigate(suite || '/app/projets', { replace: true });
    return;
  }

  // ── Chargement du module ──────────────────────────────────────────────
  // On garde la page précédente à l'écran pendant l'import : au-delà de ~160 ms
  // seulement, on affiche un état d'attente. En dessous, le remplacement est
  // instantané et un squelette ne ferait que clignoter.
  const slow = setTimeout(() => { if (token === nav) showLoading(); }, 160);

  let mod;
  try {
    mod = await r.loader();
  } catch (e) {
    clearTimeout(slow);
    if (token !== nav) return;
    console.error('[router] chargement', r.path, e);
    renderFailure("Cette page n'a pas pu être chargée.", e);
    return;
  }
  clearTimeout(slow);
  if (token !== nav) return;

  if (typeof mod?.default !== 'function') {
    renderFailure(`Le module « ${r.path} » n'exporte pas de fonction de rendu.`);
    return;
  }

  // ── Démontage de la page précédente ───────────────────────────────────
  if (mounted && mounted !== mod && typeof mounted.destroy === 'function') {
    try { mounted.destroy(); } catch (e) { console.error('[router] destroy', e); }
  }
  mounted = mod;

  // ── Habillage ─────────────────────────────────────────────────────────
  mountChrome({ ...CHROME_DEFAULT, ...(opts.chrome || {}), ...(mod.chrome || {}) });
  resetStudioRoot();

  // ── Titre ─────────────────────────────────────────────────────────────
  const raw = typeof mod.title === 'function' ? mod.title(params) : mod.title;
  document.title = raw
    ? (String(raw).includes(BRAND.name) ? String(raw) : `${raw} — ${BRAND.name}`)
    : `${BRAND.name} — ${BRAND.baseline}`;

  // ── Contenu ───────────────────────────────────────────────────────────
  const main = el('#main') || document.body;
  clear(main);
  main.removeAttribute('data-state');

  const payload = {
    ...(opts.params || {}),
    ...params,
    query: new URLSearchParams(location.search),
    hash: location.hash,
    path
  };

  try {
    await mod.default(main, payload);
  } catch (e) {
    if (token !== nav) return;
    console.error('[router] rendu', r.path, e);
    renderFailure("Cette page n'a pas pu s'afficher.", e);
    return;
  }
  if (token !== nav) return;

  // ── Défilement ────────────────────────────────────────────────────────
  // Navigation avant : en haut, ou sur l'ancre demandée. Retour arrière : on
  // rend à l'utilisateur la position exacte qu'il avait quittée.
  if (pop) {
    const y = scrollMem.get(lastKey) ?? 0;
    requestAnimationFrame(() => window.scrollTo(0, y));
  } else {
    scrollToHash(location.hash);
  }
}

/* ------------------------------------------------------------------ *
 * Écrans de service                                                   *
 * ------------------------------------------------------------------ */

function surface() {
  const main = el('#main') || document.body;
  clear(main);
  return main;
}

function showLoading() {
  ensureStyles();
  const main = surface();
  main.dataset.state = 'loading';
  main.appendChild(
    h('div.rt-wait', { role: 'status', 'aria-live': 'polite' },
      h('div.rt-wait__bar', h('i')),
      h('p.rt-wait__txt', { text: 'Chargement…' })
    )
  );
}

function renderNotFound() {
  ensureStyles();
  mountChrome(CHROME_DEFAULT);
  resetStudioRoot();
  mounted = null;
  document.title = `Page introuvable — ${BRAND.name}`;

  const main = surface();
  main.appendChild(
    h('section.rt-stop',
      h('p.rt-stop__code', { text: '404' }),
      h('h1.rt-stop__title', { text: 'Cette page a été décanettée.' }),
      h('p.rt-stop__lead', {
        text: "L'adresse n'existe pas, ou plus. Le studio, lui, est toujours à sa place."
      }),
      h('div.rt-stop__act',
        h('button.btn.btn--ghost', { type: 'button', text: '← Revenir en arrière', onclick: () => back() }),
        h('a.btn.btn--primary', { href: isAuthed() ? '/app' : '/', text: isAuthed() ? 'Ouvrir le studio' : "Retour à l'accueil" })
      )
    )
  );
}

function renderFailure(message, error) {
  ensureStyles();
  mountChrome(CHROME_DEFAULT);
  resetStudioRoot();
  mounted = null;
  if (error) toastError(error);

  const main = surface();
  main.appendChild(
    h('section.rt-stop',
      h('p.rt-stop__code', { text: '!' }),
      h('h1.rt-stop__title', { text: 'Panne de courant' }),
      h('p.rt-stop__lead', { text: message }),
      h('div.rt-stop__act',
        h('button.btn.btn--primary', { type: 'button', text: 'Réessayer', onclick: () => render({ pop: false }) }),
        h('a.btn.btn--ghost', { href: '/', text: "Retour à l'accueil" })
      )
    )
  );
}

/* Styles des écrans de service. Ils doivent exister même si une feuille de
   style manque à l'appel — c'est justement quand ça casse qu'on les voit.
   Jetons uniquement, aucune couleur littérale. */
function ensureStyles() {
  if (document.getElementById('sc-router-css')) return;
  document.head.appendChild(h('style#sc-router-css', {
    text: `
.rt-wait{max-width:var(--maxw-narrow);margin:var(--s9) auto;padding:0 var(--s5);text-align:center}
.rt-wait__bar{height:14px;border:var(--bd);border-radius:var(--r-pill);background:var(--surface);
  box-shadow:var(--sh-xs);overflow:hidden}
.rt-wait__bar>i{display:block;height:100%;width:35%;background:var(--acc);
  animation:rt-slide 1.1s var(--ease) infinite}
@keyframes rt-slide{0%{transform:translateX(-110%)}100%{transform:translateX(320%)}}
.rt-wait__txt{margin-top:var(--s3);font-size:var(--fs-sm);color:var(--ink-mute);letter-spacing:.04em;
  text-transform:uppercase}
.rt-stop{max-width:var(--maxw-narrow);margin:var(--s9) auto var(--s10);padding:var(--s7) var(--s5);
  text-align:center;background:var(--surface);border:var(--bd-lg);border-radius:var(--r-lg);
  box-shadow:var(--sh-lg)}
.rt-stop__code{font-family:var(--font-display);font-size:var(--fs-4xl);line-height:var(--lh-tight);
  letter-spacing:-.04em;color:var(--acc);-webkit-text-stroke:2px var(--ink);margin:0}
.rt-stop__title{font-family:var(--font-display);font-size:var(--fs-2xl);letter-spacing:-.02em;
  margin:var(--s3) 0 var(--s2)}
.rt-stop__lead{color:var(--ink-soft);margin:0 auto;max-width:46ch}
.rt-stop__act{display:flex;gap:var(--s3);justify-content:center;flex-wrap:wrap;margin-top:var(--s6)}
`
  }));
}
